import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchSupportOverview, type SupportOverview } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A2.07 — Support dashboard'i. `/admin/tickets` oynasi ustidan JONLI: holat va
 * prioritet taqsimoti + kategoriya + so'nggi tiketlar. Yig'indi server oynasidan
 * (top 300) hisoblanadi — alohida agregatsiya-endpoint KUTMAYDI.
 */
export function SupportDashboard() {
  const t = useT()
  const [data, setData] = useState<SupportOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchSupportOverview(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  const openCount = data ? (data.by_status.open ?? 0) + (data.by_status.pending ?? 0) : 0
  const maxStatus = data ? Math.max(1, ...Object.values(data.by_status)) : 1

  return (
    <>
      <PageHeader title={t.supDash.title} screenId="A2.07" description={t.supDash.note} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
          <KPI label={t.supDash.total} value={data?.total} loading={loading} accent />
          <KPI label={t.supDash.openish} value={openCount} loading={loading} warn={openCount > 0} />
          <KPI label={t.supDash.categories} value={data?.by_category.length} loading={loading} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.supDash.byStatus}>
            {loading ? (
              <BarsSkeleton />
            ) : !data || !Object.keys(data.by_status).length ? (
              <Empty t={t.dash.noData} />
            ) : (
              <div className="space-y-1.5">
                {Object.entries(data.by_status)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <div key={status} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 truncate text-sm text-secondary">{status}</span>
                      <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-3">
                        <div
                          className="h-full rounded bg-accent/70"
                          style={{ width: `${Math.max(2, (count / maxStatus) * 100)}%` }}
                        />
                      </div>
                      <span className="w-12 shrink-0 text-right font-mono text-sm tabular text-primary">
                        {count.toLocaleString()}
                      </span>
                    </div>
                  ))}
              </div>
            )}

            {!loading && data && Object.keys(data.by_priority).length > 0 && (
              <div className="mt-3 border-t border-border-subtle pt-3">
                <div className="mb-1.5 text-xs text-tertiary">{t.supDash.byPriority}</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(data.by_priority)
                    .sort((a, b) => b[1] - a[1])
                    .map(([p, c]) => (
                      <span
                        key={p}
                        className="rounded border border-border-default bg-surface-3 px-2 py-0.5 text-xs text-secondary"
                      >
                        {p} · <span className="font-mono tabular text-primary">{c}</span>
                      </span>
                    ))}
                </div>
              </div>
            )}
          </Panel>

          <Panel title={t.supDash.recent}>
            {loading ? (
              <BarsSkeleton />
            ) : !data || !data.recent.length ? (
              <Empty t={t.dash.noData} />
            ) : (
              <div className="divide-y divide-border-subtle">
                {data.recent.map((tk) => (
                  <Link
                    key={tk.id}
                    to={`/support/${tk.id}`}
                    className="flex items-center justify-between gap-2 py-1.5 text-sm hover:bg-surface-3"
                  >
                    <span className="min-w-0 flex-1 truncate text-primary">{tk.subject || `#${tk.id}`}</span>
                    <span className="shrink-0 text-tertiary">{tk.category}</span>
                    <span className="w-16 shrink-0 text-right text-xs text-secondary">{tk.status}</span>
                  </Link>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
  warn,
}: {
  label: string
  value: number | undefined
  loading: boolean
  accent?: boolean
  warn?: boolean
}) {
  const tone = warn ? 'text-warning' : accent ? 'text-accent' : 'text-primary'
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${tone}`}>{value?.toLocaleString() ?? '—'}</div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

function BarsSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  )
}

function Empty({ t }: { t: string }) {
  return <div className="py-6 text-center text-sm text-tertiary">{t}</div>
}
